import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getCertificate, type Certificate } from "../lib/firebase";
import { avatarGrid, cleanAvatar, palette } from "../lib/avatar";

const W = 1600, H = 1130;
const PROGRAMS: Record<string, { title: string; detail: string }> = {
  amf: { title: "AI Money Factory", detail: "เรียนครบ 7 Module · ทำ Quiz และส่งการบ้านครบ · อนุมัติโดยดิว" },
};

/** Public certificate page (/cert/<id>): anyone with the link can verify it. Drawn on a canvas so the
 *  student can download it as an image (web fonts are usable on canvas once loaded). */
export function CertificatePage() {
  const { id = "" } = useParams();
  const [cert, setCert] = useState<Certificate | null | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    getCertificate(id).then(setCert).catch(() => setCert(null));
  }, [id]);

  useEffect(() => {
    if (cert?.status !== "approved" || !canvas.current) return;
    const ctx = canvas.current.getContext("2d")!;
    document.fonts.ready.then(() => draw(ctx, cert));
  }, [cert]);

  if (cert === undefined) return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;
  if (!cert) return <main><h1>ไม่พบใบประกาศนียบัตรนี้</h1><p>ลิงก์อาจพิมพ์ผิด หรือใบเซอร์นี้ไม่มีอยู่จริง</p></main>;

  // Only the owner (or Dew) can read a certificate before approval; tell them it is waiting.
  if (cert.status !== "approved") return (
    <main className="container-tight">
      <h1>ใบเซอร์ยังไม่ออก</h1>
      <p>{cert.status === "pending" ? "ดิวกำลังดูงานของคุณอยู่ อนุมัติแล้วใบเซอร์จะขึ้นที่หน้านี้" : `ดิวขอให้แก้ก่อน${cert.note ? `: ${cert.note}` : ""}`}</p>
    </main>
  );

  const url = `${location.origin}/cert/${cert.id}`;
  function download() {
    const a = document.createElement("a");
    a.download = `dewviko-certificate-${cert!.id}.png`;
    a.href = canvas.current!.toDataURL("image/png");
    a.click();
  }

  return (
    <main className="container-tight cert-page">
      <p className="cert-page__verified">✓ ใบประกาศนียบัตรนี้ออกโดย Dewviko School จริง</p>
      <canvas ref={canvas} width={W} height={H} className="cert-page__canvas" aria-label={`ใบประกาศนียบัตร ${cert.name}`} />
      <div className="cert-page__actions">
        <button type="button" className="btn-primary" onClick={download}>ดาวน์โหลดรูป</button>
        <button type="button" className="btn-secondary" onClick={() => navigator.clipboard.writeText(url).then(() => setCopied(true))}>
          {copied ? "คัดลอกแล้ว ✓" : "คัดลอกลิงก์"}
        </button>
        <a className="btn-secondary" target="_blank" rel="noreferrer"
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}>แชร์ลง Facebook</a>
      </div>
    </main>
  );
}

function draw(ctx: CanvasRenderingContext2D, cert: Certificate) {
  const prog = PROGRAMS[cert.program] ?? { title: cert.program, detail: "" };
  const font = (w: number, px: number) => `${w} ${px}px "IBM Plex Sans Thai", "Noto Sans Thai", sans-serif`;
  ctx.fillStyle = "#F5F4EF";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#0E9F6E";
  ctx.lineWidth = 14;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.strokeStyle = "#14201A";
  ctx.lineWidth = 3;
  ctx.strokeRect(70, 70, W - 140, H - 140);

  ctx.textAlign = "center";
  ctx.fillStyle = "#0E9F6E";
  ctx.font = font(700, 30);
  ctx.fillText("DEWVIKO SCHOOL · CERTIFICATE OF COMPLETION", W / 2, 170);
  ctx.fillStyle = "#14201A";
  ctx.font = font(700, 74);
  ctx.fillText("ใบประกาศนียบัตร", W / 2, 280);
  ctx.fillStyle = "#5E6B63";
  ctx.font = font(400, 34);
  ctx.fillText("มอบให้แก่", W / 2, 360);

  // The student's 8-bit character, 16x16 cells.
  const cell = 11, ax = W / 2 - 8 * cell, ay = 395;
  const a = cleanAvatar(cert.avatar), colours = palette(a);
  avatarGrid(a).forEach((row, y) => row.split("").forEach((c, x) => {
    if (c === ".") return;
    ctx.fillStyle = colours[c];
    ctx.fillRect(ax + x * cell, ay + y * cell, cell, cell);
  }));

  ctx.fillStyle = "#14201A";
  ctx.font = font(700, 88);
  ctx.fillText(cert.name, W / 2, 690, W - 260);
  ctx.fillStyle = "#14201A";
  ctx.font = font(500, 40);
  ctx.fillText(`สำเร็จหลักสูตร ${prog.title}`, W / 2, 780);
  ctx.fillStyle = "#5E6B63";
  ctx.font = font(400, 28);
  ctx.fillText(prog.detail, W / 2, 835);

  const date = cert.issuedAt?.toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" }) ?? "";
  ctx.font = font(400, 26);
  ctx.textAlign = "left";
  ctx.fillText(`ออกให้วันที่ ${date}`, 140, 985);
  ctx.fillText(`ตรวจสอบได้ที่ school.dewviko.com/cert/${cert.id}`, 140, 1025);
  ctx.textAlign = "right";
  ctx.fillStyle = "#14201A";
  ctx.font = font(700, 40);
  ctx.fillText("Dewviko", W - 140, 985);
  ctx.fillStyle = "#5E6B63";
  ctx.font = font(400, 24);
  ctx.fillText("ผู้สอน AI Money Factory", W - 140, 1025);
}
